import PageLayout from '@/components/Layout/PageLayout';
import type { RetryProps } from '@/components/retryFailedQueries';
import type { ApiUser } from '@/domain/apiUsers';
import type { DomainFishType, FishType, LiceType, LocationTypeInfo } from '@/domain/cages';
import type { CageType } from '@/domain/cages/types';
import GridLayout from '@/routes/Client/Cage/pages/Reports/shared/GridLayout';

import ApiUsersComponent from './ApiUsers';
import { BioscopeLicenseContainer } from './BioscopeLicenseContainer/BioscopeContractListContainer';
import type { ClientLocation as ClientLocationType } from './ClientContainer';
import ClientLicense from './ClientLicense/ClientLicense';
import ClientLocations from './ClientLocations';
import FishGroupsOverviewContainer from './FishGroupsOverview/FishGroupsOverviewContainer';
import { IntegrationsSection } from './Integrations';
import { useClientHeading } from './useClientHeading';
import { VideoStreamSettings } from './VideoStreamSettings';

import styles from './Client.module.scss';

export interface ClientLocationProps {
    id: number;
    name: string;
    timezone: string;
    aquacultureSiteId: number;
    latitude?: number;
    longitude?: number;
    /** Null when the site has no default species recorded. The pen's own value is
     *  authoritative either way (WB-2207). */
    fishtypeid: FishType['id'] | null;
    licetypeid: LiceType['id'];
    locationtypeid: LocationTypeInfo['id'];
    fishType: FishType | null;
    location_type: LocationTypeInfo;
    locationType?: LocationTypeInfo;
    cages: ClientCageProps[];
}

export interface ClientCageProps {
    id: number;
    name: string;
    /** The pen's type, needed because every update has to send one back. */
    cageTypes?: { id: number; name: string } | null;
    /**
     * Species in THIS pen. A location can hold several (WB-2207), so the location's own
     * fish type is only the default for new pens and can differ from this.
     */
    fishTypes?: { id: number; name: string } | null;
    historical?: Date | string | null;
    current_bioscope?: {
        bioscopeId?: number | null;
    } | null;
    markHistorical: (historical: boolean) => Promise<unknown>;
}

export type ClientProps = {
    apiUsers: ApiUser[];
    addCreatedApiUser: (apiUser: ApiUser) => void;
    client: {
        id: number;
        name: string;
        archived?: boolean;
        archivedAt?: string;
        locations: ClientLocationProps[];
    };
    clientLocations: ClientLocationType[];
    cageTypesList: CageType[];
    fishTypesList: DomainFishType[];
    liceTypesList: LiceType[];
    locationTypesList: LocationTypeInfo[];
    /** Try again for the type lists, set only once one of them failed. */
    typesLoadError?: RetryProps | null;
    createManyCages: (
        locationId: number,
        cageTypeId: number,
        fishTypeId: number,
        names: string[]
    ) => Promise<unknown>;
    updateLocation: (input: {
        id: number;
        name: string;
        timezone: string;
        aquacultureSiteId?: number;
        latitude?: number;
        longitude?: number;
        locationtypeid: LocationTypeInfo['id'];
        fishtypeid: FishType['id'] | null;
        licetypeid: LiceType['id'];
    }) => Promise<unknown>;

    updateCage: (
        cageId: number,
        input: { name: string; cageTypeId: number; fishTypeId: number }
    ) => Promise<unknown>;

    createNewLocation: (input: {
        name: string;
        timezone: string;
        aquacultureSiteId?: number;
        latitude?: number;
        longitude?: number;
        locationtypeid: LocationTypeInfo['id'];
        fishtypeid: FishType['id'] | null;
        licetypeid: LiceType['id'];
    }) => Promise<unknown>;
    onUpdateClient?: (
        clientId: number,
        data: { name?: string; archived?: boolean },
        options?: { reportError?: boolean }
    ) => Promise<void>;
    refetch: () => void;
};

export default function Client(props: ClientProps) {
    const {
        apiUsers,
        addCreatedApiUser,
        client,
        clientLocations,
        cageTypesList,
        fishTypesList,
        liceTypesList,
        locationTypesList,
        typesLoadError,
        createManyCages,
        createNewLocation,
        updateLocation,
        updateCage,
        onUpdateClient,
        refetch,
    } = props;

    const heading = useClientHeading(client, onUpdateClient);

    return (
        <PageLayout
            title={heading.title}
            subtitle={heading.subtitle}
            titleAction={heading.titleAction}
            titleControl={heading.titleControl}
            className={styles.clientPage}
            actions={heading.actions}>
            <GridLayout contentLayout="1">
                <ApiUsersComponent
                    apiUsers={apiUsers}
                    addCreatedApiUser={addCreatedApiUser}
                    clientId={client.id}
                    clientLocations={clientLocations}
                />
                <BioscopeLicenseContainer clientId={client.id} />
                <ClientLocations
                    cageTypesList={cageTypesList}
                    fishTypesList={fishTypesList}
                    liceTypesList={liceTypesList}
                    locationTypesList={locationTypesList}
                    typesLoadError={typesLoadError}
                    client={client}
                    createManyCages={createManyCages}
                    createNewLocation={createNewLocation}
                    updateLocation={updateLocation}
                    updateCage={updateCage}
                    refetch={refetch}
                />
                <ClientLicense clientId={client.id} />
                <VideoStreamSettings clientId={client.id} />
                <IntegrationsSection clientId={client.id} />
                <FishGroupsOverviewContainer clientId={client.id} />
            </GridLayout>
        </PageLayout>
    );
}
